<template>
  <modal-wrapper
      :shown="true"
      :title="schedule
        ? 'Are you sure you want to schedule subscribing all of your contacts to this campaign?'
        : 'Are you sure you want to subscribe all contacts to this campaign?'"
      :classes="'action-modal-container'"
      @close-modal="$emit('close-modal')"
  >
        <div v-if="!schedule" class="col-md-6 col-md-offset-3">
            <div class="col-md-12" style="font-size: 21px; color: white; padding-bottom: 20px">
              This will subscribe all your contacts to this campaign.
              Please type "SUBSCRIBE ALL" below and click the 'Subscribe All' button to confirm.
              Please note that this will also affect any pending subscribers.
            </div>

            <input
                class="col-md-12"
                type="text"
                style="border-radius: 5px"
                placeholder="enter confirmation text"
                id="confirm-input"
                required
            />
            <div
                v-if="confirmationError"
                class="col-md-12"
                style="padding: 10px; font-size: 18px; color: red; padding-bottom: 20px"
            >
                Error: To confirm you want to subscribe all of your contacts, type 'SUBSCRIBE ALL' above and try again.
            </div>
        </div>

        <div v-else>
            <div class="col-md-12" style="font-size: 21px; color: white; padding-bottom: 20px">
                This will affect all contacts that are not currently subscribed. This includes currently pending contacts. It does not affect any contacts marked as unsubscribed.
            </div>
            <date-picker
                v-model="dateSelected"
                lang="en"
                type="datetime"
                width="480"
                input-class="date-time-picker"
                format="MM/DD/YYYY hh:mm A"
                :not-before="convertUtcDateToUserTimezoneMoment(new Date().toISOString().replace('T', ' ').slice(0, 19), 'YYYY-MM-DD HH:mm:ss', timezone).format('YYYY-MM-DD HH:mm:ss')"
                :clearable="false"
                :time-picker-options="{
                      start: '00:00',
                      step: '00:60',
                      end: '24:00',
                    }"
            >
            </date-picker
            ><br />
            <p class="text-white">
                Time zone: <b>{{ formattedTimezone }}</b>
            </p>
            <p class="date-time-caption">Click on the date above to change it.</p>
        </div>

        <button
            @click="subscribeAllContacts(campaign, schedule ? dateSelected : null)"
            class="btn btn-danger btn-lg modal-action-button warning"
            v-text="schedule ? 'Schedule Subscribe All' : 'Subscribe All Now'"
        />

        <button
            v-if="!schedule"
            id="immediateActionButton"
            @click="() => schedule = !schedule"
            :class="'btn-launch-immediately modal-action-button'"
        >
          Schedule Subscribe All At A Future Date
        </button>
    </modal-wrapper>
</template>

<script>
import ModalWrapper from '../modal/ModalWrapper';
import ModalProcessingIndicator from '../modal/ModalProcessingIndicator';
import DatePicker from 'vue2-datepicker';
import {convertLocalDateToServerMoment, convertServerDateToLocalMoment, convertUtcDateToUserTimezoneMoment } from '../../utility/date/date-converter';
import axios from "axios";
import * as Sentry from "@sentry/vue";

export default {
  name: 'subscribe-contact-modal',
  components: {ModalWrapper, ModalProcessingIndicator, DatePicker},
  data() {
    // convert server date to user timezone, and current time to user timezone
    let launchDate = convertServerDateToLocalMoment(this.campaign.launch, 'YYYY-MM-DD HH:mm:ss', this.timezone)
    let subscribeDate = convertUtcDateToUserTimezoneMoment(new Date().toISOString().replace('T', ' ').slice(0, 19), 'YYYY-MM-DD HH:mm:ss', this.timezone)
    // if the current time is greater than the launch time, add one day
    if (subscribeDate.format('HH:mm:ss') > launchDate.format('HH:mm:ss')) {
      subscribeDate.add(1, 'days');
    }
    // set the subscribe time to campaign launch time
    subscribeDate.set('hour', launchDate.get('hour'));
    subscribeDate.set('minute', launchDate.get('minute'));
    subscribeDate.set('second', launchDate.get('second'));
    return {
      lowestDate: new Date().toString(),
      confirmationError: false,
      processing: false,
      currentTime: convertUtcDateToUserTimezoneMoment(new Date().toISOString().replace('T', ' ').slice(0, 19), 'YYYY-MM-DD HH:mm:ss', this.timezone).format('YYYY-MM-DD HH:mm:ss'),
      dateSelected: subscribeDate.format('YYYY-MM-DD HH:mm:ss'),
      schedule: false,
    };
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    timezone: {
      type: String,
      default: '',
    },
  },
  methods: {
    convertUtcDateToUserTimezoneMoment,
    subscribeAllContacts(campaign, subscribeAt) {
      if (!this.schedule && document.getElementById('confirm-input').value !== 'SUBSCRIBE ALL') {
        this.confirmationError = true;
        return;
      }
      this.confirmationError = false;
      axios
          .post("/api/?entity=campaigns&action=subscribeAllContacts", {
            campaignId: campaign.id,
            table: 'contacts',
            subscribeAt: subscribeAt
                ? convertLocalDateToServerMoment(subscribeAt, 'YYYY-MM-DD HH:mm:ss', this.timezone).format('YYYY-MM-DD HH:mm:ss')
                : null
          })
          .then(response => {
            this.handleErrors(response.data);
            this.$emit('close-modal');
            window.location.reload();
          })
          .catch(error => {
            const {data} = error.response;
            if (!this.handleErrors(data)) {
              $.notify('Request failed! Try again later.', 'error');
              Sentry.captureException(error);
            }
            this.$emit('close-modal');
          });
    },
    handleErrors(data) {
      if (data && Array.isArray(data.validation_errors) && data.validation_errors.length) {
        self.$emit('vue-app-errors', data.validation_errors);
        $.notify('Please correct errors and try again!', 'warning');
        return true;
      } else if (data && data.errors) {
        if (data.errors.launch) {
          $.notify(data.errors.launch, 'warning');
        }
        else if (data.errors.inactive_agent) {
          $.notify('You must enter an active agent code.', 'warning');
        } else if (data.errors.no_plan) {
          $.notify('You must subscribe to a plan. Redirecting to plans page.', 'warning');
          setTimeout(() => {
            window.location = '/plans';
          }, 1500);
        } else {
          $.notify(JSON.stringify(data.errors), 'warning');
        }
        return true;
      }
      return false;
    },
  },
  computed: {
    formattedTimezone() {
      return (this.timezone || moment.tz.guess()).replace('_', ' ');
    },
  },
};
</script>

<style lang="scss" scoped>
    pre {
        text-align: left;
    }

    .modal-wrapper {
      overflow-y: scroll;
    }
</style>
